// This is required to dynamically render the fields based on the type for Appearance Page

import React from "react";
import {
  Select,
  MenuItem,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  TextField,
} from "@mui/material";
import ColorPicker from "../ColorPicker";
import FileUpload from "../FileUpload/FileUpload";
import { SLATE, INDIGO } from "../../constants/Colors";

const INPUT_SX = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

const DynamicFormField = ({
  field,
  value,
  onChange,
  onImmediateChange,
  onBlur,
  imageLabelStyleVariant,
  endpoint
}) => {
  const effectiveValue = value !== undefined ? value : field.defaultValue;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = value;
    if(type == "number"){
      finalValue = parseInt(value);
    }else if(type == "checkbox"){
      finalValue = checked;
    }
    onChange({
      target: {
        name,
        value: finalValue
      },
    });
  };

  switch (field.type) {
    case "text":
    case "number":
      return (
        <TextField
          label={field.label}
          placeholder={field.placeholder || field.label}
          value={effectiveValue}
          name={field.name}
          type={field.type}
          onChange={handleInputChange}
          onBlur={onBlur}
          fullWidth
          size="small"
          sx={INPUT_SX}
        />
      );
    case "color":
      return (
        <ColorPicker
          label={field.label}
          value={effectiveValue}
          name={field.name}
          onChange={onChange}
          onBlur={onBlur}
        />
      );
    case "image":
      return (
        <FileUpload
          field={field}
          buttonVariant=""
          onChange={onImmediateChange}
          endpoint={endpoint}
          imageLabelStyleVariant={imageLabelStyleVariant}
        />
      );
    case "select":
      return (
        <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={effectiveValue || field.options[0]}
              onChange={onChange}
              onBlur={onBlur}
              name={field.name}
              sx={{
                borderRadius: "8px",
                fontSize: "0.875rem",
                "& fieldset": { borderColor: SLATE[300] },
                "&:hover fieldset": { borderColor: SLATE[400] },
                "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
              }}
            >
              {field.options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
      );
    case "radio":
      return (
        <RadioGroup
            value={effectiveValue || ""}
            name={field.name}
            onChange={onChange}
            onBlur={onBlur}
            row
          >
            {field.options.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={
                  <Radio
                    sx={{
                      color: SLATE[400],
                      "&.Mui-checked": { color: INDIGO[600] },
                    }}
                  />
                }
                label={option}
              />
            ))}
          </RadioGroup>
      );
    case "boolean":
      return (
        <Switch
            checked={Boolean(effectiveValue)}
            onChange={handleInputChange}
            name={field.name}
            onBlur={onBlur}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: INDIGO[600] },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: INDIGO[400] },
            }}
          />
      );
    default:
      return null;
  }
};

export default DynamicFormField;
